import { useState, useMemo } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from "material-react-table";
import {
    Button,
    Grid,
    Paper,
    Typography,
    TextField,
    Box,
} from "@mui/material"
import { useListaCEPService, useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles"
import { useAppSelector } from "../../../../../../hooks/store-hooks"
import { useEffect } from "react"
// Icons
import DeleteIcon from '@mui/icons-material/Delete'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import { IListaCEP } from "../../../../../../../application/models/geo-veles/IListaCEP";

// Interface para os dados de CEP
interface CepData {
    id: number;
    cep: string;
}

// Interface para listas filtradas
interface ListaFiltrada {
    lista: IListaCEP;
    cepsFiltrados: CepData[];
    nomeCorresponde: boolean;
}

// Componente individual para cada lista
const ListaCEPVirtualTable = ({
    lista,
    cepsFiltrados,
    mostraTodosCeps
}: {
    lista: IListaCEP;
    cepsFiltrados: CepData[];
    mostraTodosCeps: boolean;
}) => {
    const columns = useMemo<MRT_ColumnDef<CepData>[]>(
        () => [
            {
                accessorKey: 'cep',
                header: 'CEP',
                size: 150,
                Cell: ({ cell }) => (
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem'
                        }}
                    >
                        {cell.getValue<string>()}
                    </Typography>
                ),
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns,
        data: cepsFiltrados,
        enablePagination: false,
        enableRowNumbers: false,
        enableToolbarInternalActions: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        // Configurações de virtualização para performance
        enableRowVirtualization: true,
        enableStickyHeader: true,
        // Ajustes de layout responsivo
        layoutMode: 'grid',
        // Configurações de desempenho
        defaultDisplayColumn: {
            enableResizing: false,
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: '400px',
                minHeight: '200px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                height: '40px',
            },
        },
        muiTableBodyCellProps: {
            sx: {
                padding: '8px 16px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
    });

    const totalCeps = useMemo(() => {
        return (lista.CEPs || []).length;
    }, [lista]);

    return (
        <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexShrink: 0 }}>
                <FormatListBulletedIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                    {lista.Nome || `Lista ${lista.IdVelesGeoListaCEP}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {mostraTodosCeps
                        ? `${totalCeps} CEP(s)`
                        : `${cepsFiltrados.length} de ${totalCeps} CEP(s)`
                    }
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <MaterialReactTable table={table} />
            </Box>
        </Paper>
    );
};

export const RecorteListaCEPFilterPreview = () => {
    // State
    const [searchTerm, setSearchTerm] = useState("");
    const { editRecorteExtracaoForm } = useAppSelector((state) => state.recorteExtracao)
    const { listasCEP } = useAppSelector((state) => state.listaCEP)
    // Hook
    const { removeEditRecorteFormFiltros } = useRecortesExtracaoService()
    const { setupListaCEP } = useListaCEPService()

    const hasListasCEP = editRecorteExtracaoForm.fields.Filtros?.listasCEP &&
        editRecorteExtracaoForm.fields.Filtros.listasCEP.length > 0;

    // Busca as listas completas baseado nos IDs selecionados
    const listasCarregadas = useMemo(() => {
        if (!hasListasCEP) return [];

        const listasDoRedux = editRecorteExtracaoForm.fields.Filtros?.listasCEP;

        return listasCEP.filter((lista: IListaCEP) =>
            listasDoRedux?.includes(lista.IdVelesGeoListaCEP)
        );
    }, [hasListasCEP, editRecorteExtracaoForm.fields.Filtros?.listasCEP, listasCEP]);

    // Filtra todas as listas baseado no termo de busca geral
    const listasFiltradas = useMemo((): ListaFiltrada[] => {
        if (!searchTerm.trim()) {
            // Se não há busca, mostra todas as listas com todos os CEPs
            return listasCarregadas.map((lista: IListaCEP) => {
                const ceps = (lista.CEPs || []).map((cep: string, index: number) => ({
                    id: index,
                    cep,
                }));
                return {
                    lista,
                    cepsFiltrados: ceps,
                    nomeCorresponde: true,
                };
            });
        }

        const term = searchTerm.toLowerCase();
        const termAsNumber = !isNaN(Number(term)) ? Number(term) : null;

        return listasCarregadas
            .map((lista: IListaCEP) => {
                const nomeCorresponde = lista.Nome?.toLowerCase().includes(term) ?? false;

                const todosCeps = (lista.CEPs || []).map((cep: string, index: number) => ({
                    id: index,
                    cep,
                }));

                const cepsFiltrados = todosCeps.filter((item: CepData) => {
                    if (termAsNumber !== null) {
                        const cepAsNumber = Number(item.cep);
                        if (!isNaN(cepAsNumber) && cepAsNumber === termAsNumber) {
                            return true;
                        }
                    }
                    return item.cep.toLowerCase().includes(term);
                });

                return { lista, cepsFiltrados, nomeCorresponde };
            })
            .filter((listaFiltrada: ListaFiltrada) =>
                listaFiltrada.nomeCorresponde || listaFiltrada.cepsFiltrados.length > 0
            );
    }, [listasCarregadas, searchTerm]);

    // Calcula o layout responsivo baseado na quantidade de listas
    const getGridLayout = () => {
        const count = listasFiltradas.length;
        if (count === 0) return { xs: 12 };
        if (count === 1) return { xs: 12 };
        return { xs: 12, md: 6 }; // Máximo 2 por linha
    };

    const handleRemoveFilter = () => {
        removeEditRecorteFormFiltros({ filtro: 'listasCEP' });
    };

    // Use Effect
    useEffect(() => {
        setupListaCEP()
    }, [setupListaCEP])

    if (!hasListasCEP) {
        return null;
    }

    return (
        <Grid item container xs={12} spacing={2} mb={2} p={2}>
            <Paper elevation={3} sx={{ padding: 2, width: "100%" }}>
                <Grid item xs={12} container justifyContent="space-between" alignItems="center" my={1}>
                    <Grid item xs={6}>
                        <Typography
                            variant="body1"
                            color="primary"
                            fontWeight="bold"
                            textTransform="uppercase"
                        >
                            Listas de CEP ({listasCarregadas.length})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} container justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={handleRemoveFilter}
                        >
                            Remover filtro
                        </Button>
                    </Grid>
                </Grid>

                {/* Barra de busca GERAL única */}
                <TextField
                    size="small"
                    placeholder="Pesquisar por nome da lista ou CEP em todas as listas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        ),
                    }}
                />

                {/* Grid responsivo com listas lado a lado (máximo 2 por linha) */}
                <Grid container spacing={2}>
                    {listasFiltradas.length > 0 ? (
                        listasFiltradas.map((listaFiltrada: ListaFiltrada) => (
                            <Grid item {...getGridLayout()} key={listaFiltrada.lista.IdVelesGeoListaCEP.toString()}>
                                <ListaCEPVirtualTable
                                    lista={listaFiltrada.lista}
                                    cepsFiltrados={listaFiltrada.cepsFiltrados}
                                    mostraTodosCeps={!searchTerm.trim()}
                                />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                    Nenhuma lista ou CEP encontrado para "{searchTerm}"
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    )
}